/* eslint-disable camelcase */
import { useLocation } from 'react-router-dom'
import { useEffect, useMemo } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { useDispatch } from 'react-redux'
import { useUser } from 'builder/hooks/useUser'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'

import { actions as uiActions } from 'builder/modules/ui'
import { actions as userAction } from 'builder/modules/user'
import { actions as panelAction } from 'builder/modules/panel'
import { actions as renderingActions } from 'builder/modules/rendering'
import { useAutoApplyUpsellModal } from 'builder/components/AutoApplyUpsellModal/useAutoApplyUpsellModal'
import { selectors as jobSearchSelectors } from 'builder/modules/jobSearch'
import { useAutoApplyResumeDistributionModal } from 'builder/features/modals/useAutoApplyResumeDistributionModal'

const ALLOWED_ROUTES = ['/resumes', '/job-search', '/tr-documents']
const DOCUMENT_ROUTES = ['/resumes', '/tr-documents']

export function useAppWelcomeModal() {
  const user = useUser()
  const dispatch = useDispatch()
  const { features } = useFeaturesConfig()
  const aaRdDualModal = useAutoApplyResumeDistributionModal()
  const aaUpsellModal = useAutoApplyUpsellModal()
  const autoApplyBubbles = useTypedSelector(jobSearchSelectors.autoApplyBubbles)

  const uiStates = user?.uiStates
  const { pathname } = useLocation()
  const trDocuments = useTypedSelector(store => store.panel.trDocuments)
  const documents = useTypedSelector(store => store.panel.documents)
  const documentsStatus = useTypedSelector(store => store.panel.documentsStatus)

  const resumeByExpert = Object.values(trDocuments).find(({ doc_type }) => doc_type === 'resume')
  const hasResumeByExpert = resumeByExpert ? Object.keys(resumeByExpert).length > 0 : false
  const hasResumeDistribution = user?.scopes?.resumeDistribution
  const refreshedResume = documents.find(
    ({ type, name }) => type === 'resume' && name?.toLowerCase().includes('refresh'),
  )

  const aaBubleStepRunning =
    window.location.pathname === '/app/job-search' &&
    (!autoApplyBubbles.initialized || autoApplyBubbles.userStartedAutoApplyBubbles)

  const isModalActive = useMemo(() => {
    const showModalOnAllowedRoutes = ALLOWED_ROUTES.includes(pathname)
    const welcomeModalWasSeen = uiStates?.welcomeModalSeen
    const docStillLoading = documentsStatus === 'LOADING' && !documents
    if (
      (docStillLoading && DOCUMENT_ROUTES.includes(pathname)) ||
      aaRdDualModal.isOpen ||
      aaUpsellModal.isOpen ||
      aaBubleStepRunning
    ) {
      return false
    }

    return !welcomeModalWasSeen && showModalOnAllowedRoutes
  }, [
    pathname,
    uiStates?.welcomeModalSeen,
    documentsStatus,
    documents,
    aaRdDualModal.isOpen,
    aaUpsellModal.isOpen,
    aaBubleStepRunning,
  ])

  const showUpsellOrDualModal = () => {
    if (
      features.autoApplyUpsellModalV4 !== null &&
      !localStorage.getItem('rd-auto-apply-dual-modal') &&
      !localStorage.getItem('auto-apply-upsell-modal')
    ) {
      if (features.autoApplyUpsellModalV4 && user?.scopes?.autoApply) {
        dispatch(uiActions.toggleAutoApplyResumeDistributionModal({ open: true }))
      } else {
        if (aaUpsellModal.showUpsellModal) {
          dispatch(renderingActions.toggleAutoApplyUpsellModal(true))
        }
      }
    }
  }

  useEffect(() => {
    if (isModalActive) {
      trackInternalEvent('see_new_welcome_modal')
    }
  }, [isModalActive])

  const closeModal = () => {
    trackInternalEvent('close_new_welcome_modal')
    dispatch(panelAction.setUIStatesConfigStatus({ welcome_modal_seen: true }))
    dispatch(userAction.setUIUserState({ welcomeModalSeen: true }))
    showUpsellOrDualModal()
  }

  return {
    showWelcomeModal: isModalActive,
    closeModal,
    documents: {
      hasResumeByExpert,
      hasResumeDistribution,
      refreshedResume,
      resumeByExpert,
    },
  }
}
